import {
  Box, Checkbox, Icon, IconName, Label, MultiSelect, TextField, Tooltip, Picker,
} from '@usealloy/component-library';
import React, { useContext } from 'react';

import { Controller, useFormContext } from 'react-hook-form';
import {
  StyledTagSettingsFormContainer,
  StyledLeftColumnContainer,
  StyledRightColumnContainer,
  StyledTextArea,
  DrawerDescription,
  DrawerLink,
} from './styles';
import { routes } from '~/helpers/routes';
import type { FormState } from './types';
import {
  getCaseTypeOptions, getCategoryOptions, getOutcomeOptions, getSuppressedUnits,
} from './helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowProps, selectFocusedAction, selectTagByName } from '~/dashboard/views/workflows/store/redux/selectors';
import { UserContext } from '~/dashboard/context';
import { MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH } from './constants';
import { isTagEphemeral } from 'flow/service/tags';
import { isTagNameUnique, validationMessages } from './validation';


export default function TagGlobalSettingsTab({
  isReadOnly = false,
}: {
  isReadOnly?: boolean;
}) {
  const { featureFlags } = useContext(UserContext);
  const {
    categories, outcomes, tagTypes, tags,
  } = useAppSelector(selectWorkflowProps);
  const focusedAction = useAppSelector(selectFocusedAction);
  const matchingTagProp = useAppSelector(selectTagByName(focusedAction?.tag?.name));

  const isCaseManagementEnabled = !!featureFlags.caseManagement;
  const isJourneysEnabled = !!featureFlags.journeys;

  const categoryOptions = React.useMemo(
    () => categories && getCategoryOptions(categories),
    [categories],
  );
  const caseTypeOptions = React.useMemo(() => tagTypes && getCaseTypeOptions(tagTypes), [tagTypes]);
  const outcomeOptions = React.useMemo(() => outcomes && getOutcomeOptions(outcomes), [outcomes]);
  const suppressionUnitOptions = getSuppressedUnits();

  const {
    register,
    control,
    watch,
    formState,
    clearErrors,
    getFieldState,
  } = useFormContext<FormState>();

  const checkedIsCaseCreating = watch('isCaseCreating');
  const checkedIsSuppressed = watch('isSuppressed');
  const selectedCaseTypeOption = watch('caseType');
  const descriptionText = watch('description') ?? '';
  const nameText = watch('name') ?? '';
  const isCaseTypeSelected = selectedCaseTypeOption && selectedCaseTypeOption.value !== 'none';
  const checkedIsAdverseActionReason = watch('isAdverseActionReason');

  return (
    <StyledTagSettingsFormContainer>
      <StyledLeftColumnContainer>
        <Controller
          name="name"
          control={control}
          rules={{
            required: {
              value: true,
              message: validationMessages.name.required,
            },
            maxLength: {
              value: MAX_NAME_LENGTH,
              message: validationMessages.name.maxLength,
            },
            validate: {
              uniqueName: (value: string) => {
                const fieldState = getFieldState('name', formState);
                if (!fieldState.isDirty) return true; // if name has not changed from default (start value) do not validate
                // Use the original tag name from the action to identify which tag to exclude
                // This works for both ephemeral tags (null IDs) and saved tags (with IDs)
                const originalTagName = focusedAction?.tag?.name ?? null;
                return isTagNameUnique(tags, value, originalTagName)
                    || validationMessages.name.uniqueName;
              },
            },
          }}
          render={({
            field: {
              onChange, onBlur, value, ref,
            },
          }) => (
            <TextField
              ref={ref}
              error={formState.errors?.name?.message}
              label="Tag Name"
              type="text"
              disabled={isReadOnly || (featureFlags.useTyphonWorkflowEndpoints
                ? !isTagEphemeral(matchingTagProp)
                : false)}
              maxLength={MAX_NAME_LENGTH}
              helpText={
                isTagEphemeral(matchingTagProp)
                  ? `${MAX_NAME_LENGTH - nameText.length} characters left`
                  : (
                    <DrawerDescription>
                      This tag is saved. Go to
                      <DrawerLink
                        className="secondary"
                        href={routes.settings.list}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Settings
                      </DrawerLink>
                      to rename.
                    </DrawerDescription>
                  )
              }
              onChange={onChange}
              onBlur={onBlur}
              value={value}
            />
          )}
        />
        <Box alignItems="flex-start" direction="column" justifyContent="flex-start">
          <Label htmlFor="tag_name">Color</Label>
          <Controller
            name="color"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Picker variant="color" onChange={onChange} value={value} disabled={isReadOnly} />
            )}
          />
        </Box>
        <StyledTextArea
          helpText={`${MAX_DESCRIPTION_LENGTH - descriptionText.length} characters left`}
          label="Description"
          maxLength={MAX_DESCRIPTION_LENGTH}
          {...register('description', {
            maxLength: {
              value: MAX_DESCRIPTION_LENGTH,
              message: validationMessages.description.maxLength,
            },
          })}
          disabled={isReadOnly}
        />
      </StyledLeftColumnContainer>
      <StyledRightColumnContainer>
        <Box direction="column">
          <Box direction="row" justifyContent="flex-start">
            <Label htmlFor="tag_category">Category</Label>
            <Tooltip
              content={(
                <Box direction="column" justifyContent="center">
                  <p>Categorize tags to keep your policy organized</p>
                  <p>and separated by concerns.</p>
                </Box>
              )}
              delayDuration={0}
            >
              <Icon icon={IconName.Info} size="small" />
            </Tooltip>
          </Box>
          <Controller
            name="category"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                id="tag_category"
                isMulti={false}
                isSearchable
                onChange={onChange}
                options={categoryOptions}
                selectedOptions={value}
                isClearable
                placeholder="select"
                isDisabled={isReadOnly}
              />
            )}
          />
        </Box>
        {isJourneysEnabled && (
          <Box direction="column">
            <Box direction="row" justifyContent="flex-start">
              <Label htmlFor="tag_outcomes">Outcomes</Label>
              <Tooltip
                content={(
                  <Box direction="column" justifyContent="center">
                    <p>Associate a tag with an outcome to provide a</p>
                    <p>{'\'reason\' for a decision in the review experience.'}</p>
                  </Box>
                )}
                delayDuration={0}
              >
                <Icon icon={IconName.Info} size="small" />
              </Tooltip>
            </Box>
            <Controller
              name="outcomes"
              control={control}
              rules={{
                deps: 'isAdverseActionReason',
                required: {
                  value: checkedIsAdverseActionReason,
                  message: validationMessages.outcomes.required,
                },
              }}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  id="tag_outcomes"
                  isSearchable
                  onChange={selectedValue => {
                    onChange(selectedValue);
                    // Clear error when user interacts with the field
                    if (formState.errors?.outcomes) {
                      clearErrors('outcomes');
                    }
                  }}
                  options={outcomeOptions}
                  placeholder="select"
                  selectedOptions={value}
                  error={formState.errors?.outcomes?.message}
                  isDisabled={isReadOnly}
                />
              )}
            />
          </Box>
        )}
        <Box alignItems="center" direction="row" justifyContent="flex-start">
          <Controller
            control={control}
            name="isAdverseActionReason"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                checked={value}
                label="This tag represents an adverse action reason."
                onChange={evt => {
                  const { checked } = evt.target;
                  onChange(evt);
                  // Clear outcomes error when checkbox is unchecked
                  if (!checked && formState.errors?.outcomes) {
                    clearErrors('outcomes');
                  }
                }}
                disabled={isReadOnly}
              />
            )}
          />
          <Tooltip
            content={(
              <Box direction="column" justifyContent="center">
                <p>Checking this box will add this tag</p>
                <p>to the final application response</p>
                <p>as an adverse action reason.</p>
              </Box>
          )}
            delayDuration={0}
          >
            <Icon icon={IconName.Info} size="small" />
          </Tooltip>
        </Box>
        {isCaseManagementEnabled && (
          <>
            <Box alignItems="center" direction="row" justifyContent="flex-start">
              <Controller
                control={control}
                name="isCaseCreating"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    label="This tag generates a case"
                    onChange={evt => {
                      const { checked } = evt.target;
                      onChange(evt);
                      // Clear caseType error when checkbox is unchecked
                      if (!checked && formState.errors?.caseType) {
                        clearErrors('caseType');
                      }
                    }}
                    disabled={isReadOnly}
                  />
                )}
              />
              <Tooltip
                content={(
                  <Box direction="column" justifyContent="center">
                    <p>If an evaluation has a case-generating tag,</p>
                    <p>a case will be generated for this entity and</p>
                    <p>will be available for review in Case Management.</p>
                    <p>If a case is already open for an entity,</p>
                    <p>this tag will be added to that case.</p>
                  </Box>
                )}
                delayDuration={0}
              >
                <Icon icon={IconName.Info} size="small" />
              </Tooltip>
            </Box>
            <Controller
              name="caseType"
              control={control}
              rules={{
                deps: 'isCaseCreating',
                required: {
                  value: checkedIsCaseCreating,
                  message: validationMessages.caseType.required,
                },
              }}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  isDisabled={isReadOnly || !checkedIsCaseCreating}
                  isMulti={false}
                  isSearchable
                  label="Case Type"
                  onChange={selectedValue => {
                    onChange(selectedValue);
                    // Clear error when user interacts with the field
                    if (formState.errors?.caseType) {
                      clearErrors('caseType');
                    }
                  }}
                  options={caseTypeOptions}
                  selectedOptions={value}
                  isClearable
                  placeholder="select"
                  error={formState.errors?.caseType?.message}
                />
              )}
            />
            <Box alignItems="center" direction="row" justifyContent="flex-end" gap={6}>
              <Controller
                control={control}
                name="isSuppressed"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    disabled={!checkedIsCaseCreating || !isCaseTypeSelected}
                    checked={value}
                    label="Suppress tag for"
                    onChange={onChange}
                  />
                )}
              />
              <Box width="auto" gap={2}>
                <Box width="80px">
                  <Controller
                    name="suppressedValue"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        disabled={
                        !checkedIsCaseCreating || !isCaseTypeSelected || !checkedIsSuppressed
                      }
                        type="number"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </Box>
                <Box width="100px">
                  <Controller
                    name="suppressedUnit"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <MultiSelect
                        isDisabled={
                          !checkedIsCaseCreating || !isCaseTypeSelected || !checkedIsSuppressed
                        }
                        isMulti={false}
                        onChange={onChange}
                        options={suppressionUnitOptions}
                        selectedOptions={value}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </StyledRightColumnContainer>
    </StyledTagSettingsFormContainer>
  );
}

